import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'

import { setIsPro, logIn } from '../../store/actions'
import { useGoogleLogin } from '@react-oauth/google'
import { authApi, profileApi } from 'api'

import { AlertMessage } from 'components/AlertMessage'
import { FadeCarousel } from 'components/FadeCarousel'
import FormInputWithLabel from 'components/Form/FormInputWithLabel/FormInputWithLabel'
import { AuthRow } from 'components/ui/AuthRow'
import { AuthCol } from 'components/ui/AuthCol'
import { AuthHeader } from 'components/ui/AuthHeader'
import { AuthContent } from 'components/ui/AuthContent'
import { AuthItem } from 'components/ui/AuthItem'
import { Button } from 'components/ui/Button'
import { BaseLink } from 'components/ui/BaseLink'
import { InputLabel } from 'components/ui/InputLabel'
import { WhyNeedAccount } from 'components/WhyNeedAccount'
import { FormCustomSelect } from 'components/Form'

import { professionalTypeOptions, SUBSCRIPTION_PLAN } from 'utils/constants'

import img1 from 'i/carousel_images/Influencer.png'
import img2 from 'i/carousel_images/dr-30s-scrubs.png'

const signupSchema = Yup.object().shape({
  displayName: Yup.string()
    .required('Enter name, company or brand')
    .min(3, 'Name is too short')
    .max(80, 'Name is too long'),
  channelName: Yup.string()
    .required('Slug is required')
    .min(3, 'Slug is too short')
    .max(80, 'Slug is too long'),
  category: Yup.string()
    .required('Professional type is required')
    .oneOf(
      ['Therapist', 'Practitioner', 'Business', 'Teacher', 'Other'],
      'Professional type is not selected'
    ),
})

const ProJoinStandardPage = (props) => {
  const [success, setSuccess] = useState(false)
  const [channelName, setChannelName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [category, setCategory] = useState('')
  const oauthRedirectUri = `https://www.talkpro.com/pro/join/standard/`

  const history = useHistory()

  const onSuccess = async (res) => {
    try {
      const result = await authApi.googleLogInPro({
        code: res?.code,
        redirect_uri: oauthRedirectUri,
      })

      if (result?.result?.access_token) {
        localStorage.setItem('token', result?.result?.access_token)
        localStorage.setItem('refreshToken', result?.result?.refresh_token)
        localStorage.setItem('isPro', true)

        props.setIsPro(true)

        await profileApi.setProInfo({
          channel_name: channelName,
          name: displayName,
          category,
          subscription_plan: SUBSCRIPTION_PLAN.standard,
        })

        const profileRes = await profileApi.getProfileInfo()
        props.logIn(profileRes?.result?.profile)

        history.push('/pro/rate', {
          channelName,
          category,
        })
      }
    } catch (err) {
      console.error('Login failed:', err)
    }
  }

  useEffect(() => {
    document.title = 'TalkPro | Join'
  }, [])

  const onFailure = async (res) => {
    console.log('Error authenticating: ', res)
    setSuccess(false)
  }

  const login = useGoogleLogin({
    onSuccess,
    onFailure,
    flow: 'auth-code',
    redirect_uri: oauthRedirectUri,
    scope:
      'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.events.readonly https://www.googleapis.com/auth/youtube.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid https://www.googleapis.com/auth/contacts.readonly',
  })

  const items = [
    { image: img1, text: 'Instant Sync with Your Calendar' },
    { image: img2, text: 'Privacy First. No Compromise' },
  ]

  return (
    <AuthRow>
      {/*<AuthCol type="gradient" />*/}
      <AuthCol>
        <FadeCarousel items={items} interval={10000} />
      </AuthCol>
      <AuthCol>
        {/*<AuthHeader
          title="Welcome to TalkPro"
          subTitle="Please tell us about yourself and what type of pro you are."
          offsetMod
          dividerMod
        />*/}
        <AuthHeader title="Welcome to TalkPro" offsetMod />
        <AuthItem>
          <AuthContent>
            Create your profile by entering your name, public link, and profession. This
            tailors your experience from the start.
          </AuthContent>
        </AuthItem>

        {/* <AuthItem variant="divider">
          <AuthContent>
            You don’t need to remember an email and password, <br /> plus we’ll send you a
            reminders before your talk
          </AuthContent>
        </AuthItem> */}
        <AuthItem variant="">
          <Formik
            initialValues={{
              displayName: '',
              channelName: '',
              category: 'Professional Type',
              agreement: false,
            }}
            validationSchema={signupSchema}
            onSubmit={async (values, { setSubmitting }) => {
              login()
              setSuccess(true)
              setSubmitting(false)
            }}
          >
            {({
              errors,
              submitForm,
              values,
              setFieldValue,
              isSubmitting,
              handleChange,
            }) => (
              <Form>
                <AuthItem variant="">
                  <div className="form_group__field_wrapper">
                    <FormInputWithLabel
                      colorV3
                      type="text"
                      label="Enter professional or company name"
                    >
                      <Field
                        className="form_group__field form_group__field--suffix_mod"
                        name="displayName"
                        id="displayName"
                        type="text"
                        placeholder=""
                        disabled={isSubmitting || success}
                        onChange={({ target: { value } }) =>
                          setFieldValue('displayName', value) && setDisplayName(value)
                        }
                      />
                      <ErrorMessage name="displayName">
                        {(msg) => <AlertMessage message={msg} inputMod />}
                      </ErrorMessage>
                    </FormInputWithLabel>
                  </div>
                </AuthItem>
                <AuthItem variant="">
                  {/* <AuthContent>
                    Create your TalkPro URL describing you or your business
                  </AuthContent> */}
                  <div className="form_group__field_wrapper">
                    <InputLabel variant="preffixGray">{`talkpro.com/`}</InputLabel>
                    <div className="form_group__field_block">
                      <FormInputWithLabel colorV3 type="text" label="Enter personal slug">
                        <Field
                          className="form_group__field form_group__field--suffix_mod"
                          name="channelName"
                          id="channelName"
                          type="text"
                          placeholder=""
                          disabled={isSubmitting || success}
                          onChange={({ target: { value } }) =>
                            /^[a-z0-9]{0,80}$/i.test(value) &&
                            setFieldValue('channelName', value.toLowerCase()) &&
                            setChannelName(value.toLowerCase())
                          }
                        />
                        <ErrorMessage name="channelName">
                          {(msg) => <AlertMessage message={msg} inputMod />}
                        </ErrorMessage>
                      </FormInputWithLabel>
                    </div>
                  </div>
                </AuthItem>
                <AuthItem>
                  <div className="form_group__field_block">
                    <FormCustomSelect
                      v4
                      placeholder="Type of professional"
                      options={professionalTypeOptions}
                      disabled={isSubmitting || success}
                      onChange={(e) => {
                        setFieldValue('category', e.value.name) &&
                          setCategory(e.value.name)
                      }}
                    />
                    <ErrorMessage name="category">
                      {(msg) => <AlertMessage message={msg} inputMod />}
                    </ErrorMessage>
                  </div>
                </AuthItem>
                {/* <AuthItem>
                  <Button
                    type="submit"
                    variant="default"
                    iconName="google"
                    fullWidth
                    disabled={isSubmitting || success}
                    onClick={(e) => {
                      e.preventDefault()
                      submitForm()
                    }}
                  >
                    Continue with Google
                  </Button>
                </AuthItem> */}
                <AuthItem>
                  <Button
                    type="submit"
                    variant="tertiary"
                    size="lg"
                    iconName="google_white"
                    fullWidth
                    iconTextCenter
                    disabled={isSubmitting || success}
                    onClick={(e) => {
                      e.preventDefault()
                      submitForm()
                    }}
                  >
                    Continue with Google
                  </Button>
                </AuthItem>
                {/* <AuthItem>
                  <BaseLink
                    href="/pro/login"
                    onClick={(e) => {
                      e.preventDefault()
                      history.push('/pro/login', { login: true })
                    }}
                  >
                    Already have an account?
                  </BaseLink>
                </AuthItem> */}
              </Form>
            )}
          </Formik>
        </AuthItem>
        <AuthItem variant="">
          <WhyNeedAccount />
        </AuthItem>
        <AuthItem>
          <BaseLink
            cls="link1"
            href="/pro/login"
            onClick={(e) => {
              e.preventDefault()
              history.push('/pro/login', { login: true })
            }}
          >
            Login here
          </BaseLink>
        </AuthItem>
      </AuthCol>
    </AuthRow>
  )
}

const mapStateToProps = ({ auth: { error, attributes } }) => ({
  authError: error,
  attributes,
})

const willMapDispatchToProps = {
  setIsPro,
  logIn,
}

export default connect(mapStateToProps, willMapDispatchToProps)(ProJoinStandardPage)
